// src/components/authentication/ForgotPasswordRequest.js
/* ------------------------------------------------------------------ */
/* ForgotPasswordRequest – send reset-link e-mail                      */
/* ------------------------------------------------------------------ */
/**
 * • Collects user’s e-mail → `sendPasswordResetEmail()`.
 * • Shows confirmation that a link was sent (even if the e-mail is unknown,
 *   to avoid account enumeration).
 */

import React, { useState } from "react";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { Link } from "react-router-dom";
import { firebaseApp } from "../../firebase/firebaseApp";
import { EMAIL_REGEX } from "../../utils/constants";
import usePopup from "../../hooks/usePopup";
import ErrorPopup from "../ErrorPopup";
import { useTranslation } from "react-i18next";

/* Firebase singleton */
const auth = getAuth(firebaseApp);

/* Component */
export default function ForgotPasswordRequest() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { popup, open: openPopup, close: closePopup } = usePopup();

  const handleSubmit = async (e) => {
    e.preventDefault();
    closePopup();

    if (!EMAIL_REGEX.test(email)) {
      openPopup({ message: t("forgotPassword.emailRegexError") });
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email, {
        // optional: customise Firebase-hosted page URL
        url: `${window.location.origin}/auth/reset-password`,
        handleCodeInApp: true,
      });
      setSent(true);
    } catch (err) {
      openPopup({ message: t("forgotPassword.emailSendError") });
    }
  };

  return (
    <div className="w-full max-w-2xl h-full p-4 flex flex-col justify-end md:justify-start items-center gap-4">
      {popup && <ErrorPopup popup={popup} onClose={closePopup} />}

      <img
        src="/logos/BreatheUltra_Main_White_Logo.png"
        alt="pDx logo"
        className="h-24"
      />
      <h1 className="title-text">{t("forgotPassword.title")}</h1>

      {sent ? (
        <>
          <p className="text-center">
            {t("forgotPassword.exists1")}
            <strong>{email}</strong> {t("forgotPassword.exists2")}
          </p>
          <Link to="/auth/login" className="link">
            {t("forgotPassword.back")}
          </Link>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
          <input
            type="email"
            placeholder={t("forgotPassword.email")}
            className="custom-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" className="primary-button">
            {t("forgotPassword.cta")}
          </button>
        </form>
      )}
    </div>
  );
}
